import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useModal } from 'common/hooks/useModal';
import { QueryError } from 'src/core/common/components/QueryError';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useParams } from 'src/core/common/hooks/useParams';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useUserRoles } from 'src/core/modules/app/hooks/useUserRoles';
import { CostCenterActivationModal } from 'src/core/modules/purchase-orders/pages/PurchaseOrdersPage/CostCenterActivationModal';

import { BudgetaryExercisePageEmptyState } from '../../../../components/BudgetaryExercisePageEmptyState';
import { useRedirectToBudgetaryExercise } from '../../../../containers/hooks/useRedirectToBudgetaryExercise';
import { getCurrentOrDefaultBudgetaryExercise } from '../../../../models/budgetaryExercise';
import { BudgetaryExercisePageContent } from '../BudgetaryExercisePageContent';
import { BudgetaryExercisesPageSkeleton } from './BudgetaryExercisesPageSkeleton';
import { NoCostCenter } from '../../../../components/NoCostCenter';
import { BudgetSetupGuideModalContainer } from '../../../../containers/BudgetSetupGuideModalContainer';
import { useIsUserAllowedToAccessBudgetaryExercise } from '../../../../containers/BudgetaryExercisesPageContainer/hooks/useIsUserAllowedToAccessBudgetaryExercise';
import { usePageDataQuery } from '../../../../containers/BudgetaryExercisesPageContainer/hooks/usePageDataQuery';
import { CreateBudgetaryExerciseModalContainer } from '../../../../containers/CreateBudgetaryExerciseModalContainer';
import { useRedirectToBudget } from '../../../../containers/hooks/useRedirectToBudget';
import { type PeriodRange } from '../../../../models/periodRange';

export const NewBudgetaryExercisesPage = () => {
  const redirectToBudgetaryExercise = useRedirectToBudgetaryExercise();
  const redirectToBudget = useRedirectToBudget();
  const isCostCentersActivated = useFeature(FEATURES.COST_CENTERS_ACTIVATED);
  const isBudgetByDimensionsFeatureToggleEnabled = useFeature(
    FEATURES.BUDGETS_BY_DIMENSIONS,
  );
  const pageDataQueryState = usePageDataQuery();
  const { isAdmin, isAccountOwner, isController } = useUserRoles();
  const history = useHistory();
  const companyId = useCompanyId();
  const { id: selectedBudgetaryExerciseId } = useParams(
    routes.BUDGETARY_EXERCISES.path,
  );
  const location = useLocation();
  const [selectedPeriodRange, setSelectedPeriodRange] = useState<PeriodRange>(
    location.state?.selectedPeriodRange ?? {
      key: 'overall',
    },
  );

  const isUserAllowedToAccessBudgetaryExercise =
    useIsUserAllowedToAccessBudgetaryExercise(
      selectedBudgetaryExerciseId ?? '',
    );

  const { t } = useTranslation('global');

  const [
    budgetaryExerciseCreationModal,
    showBudgetaryExerciseCreationModal,
    hideBudgetaryExerciseCreationModal,
  ] = useModal(({ isOpen }: { isOpen: boolean }) => {
    if (!isOpen) {
      return <></>;
    }
    return (
      <CreateBudgetaryExerciseModalContainer
        onHide={() => {
          hideBudgetaryExerciseCreationModal();
        }}
      />
    );
  });

  const [budgetSetupGuideModal, showSetupGuideModal, hideSetupGuideModal] =
    useModal(
      ({
        isOpen,
        hasCostCenters,
      }: {
        isOpen: boolean;
        hasCostCenters: boolean;
      }) => {
        if (!isOpen) {
          return <> </>;
        }
        return (
          <BudgetSetupGuideModalContainer
            onCloseModal={() => hideSetupGuideModal()}
            doneText={
              hasCostCenters
                ? t('budget.onboarding.defineBudgetaryPeriod')
                : t('budget.onboarding.defineCostCenters')
            }
            onDone={() => {
              if (!hasCostCenters) {
                history.push(
                  routeFor(routes.COST_CENTERS.path, {
                    company: companyId,
                  }),
                );
                return;
              }
              hideSetupGuideModal();
              if (isBudgetByDimensionsFeatureToggleEnabled) {
                // navigate to budget by dimensions creation page
                history.push(
                  routeFor(routes.SETUP_BUDGET_BY_DIMENSIONS.path, {
                    company: companyId,
                  }),
                );
              } else {
                showBudgetaryExerciseCreationModal({
                  isOpen: true,
                });
              }
            }}
          />
        );
      },
    );

  const [
    costCenterActivationModal,
    showCostCenterActivationModal,
    hideCostCenterActivationModal,
  ] = useModal(({ isOpen }: { isOpen: boolean }) => {
    return (
      <CostCenterActivationModal
        isOpen={isOpen}
        title={t('purchaseOrders.activation.modal.title', {
          name: t('purchaseOrders.activation.modal.budget'),
        })}
        description={t('purchaseOrders.activation.modal.description', {
          name: t('purchaseOrders.activation.modal.budget'),
        })}
        onClose={() => hideCostCenterActivationModal()}
      />
    );
  });

  useEffect(() => {
    if (
      pageDataQueryState.status === 'success' &&
      pageDataQueryState.data.budgetaryExercises.length > 0 &&
      !selectedBudgetaryExerciseId
    ) {
      const currentOrDefaultBudgetaryExercise =
        getCurrentOrDefaultBudgetaryExercise(
          pageDataQueryState.data.budgetaryExercises,
        );

      if (!currentOrDefaultBudgetaryExercise) {
        return;
      }

      const budgetsFromAllBudgetaryExercises =
        pageDataQueryState.data.budgetaryExercises.flatMap(
          ({ budgets }) => budgets,
        );

      // if there's only one visible budget overall, and it's in the current exercise,
      // we redirect to overview page of this budget
      const shouldRedirectToBudgetOverviewPage =
        budgetsFromAllBudgetaryExercises.length === 1 &&
        currentOrDefaultBudgetaryExercise.budgets.length === 1 &&
        !(isAdmin || isAccountOwner || isController);

      if (shouldRedirectToBudgetOverviewPage) {
        redirectToBudget(currentOrDefaultBudgetaryExercise.budgets[0].id);
      } else {
        redirectToBudgetaryExercise(currentOrDefaultBudgetaryExercise.id);
      }
    }
  }, [pageDataQueryState, selectedBudgetaryExerciseId]);

  if (pageDataQueryState.status === 'loading') {
    return <BudgetaryExercisesPageSkeleton />;
  }

  if (pageDataQueryState.status === 'error') {
    return (
      <QueryError
        queryError={pageDataQueryState.error}
        componentType="Callout"
      />
    );
  }

  const { data } = pageDataQueryState;

  const isAccountOwnerOrController = isController || isAccountOwner;

  const hasCostCenters = data.costCenters.length !== 0;

  // Rights

  const canCreateBudgets = isAccountOwnerOrController;

  const availableCostCenters = data.costCenters.filter((cc) => !cc.isDeleted);

  if (!isUserAllowedToAccessBudgetaryExercise) {
    return null;
  }

  if (data.budgetaryExercises.length === 0) {
    return (
      <>
        <BudgetaryExercisePageEmptyState
          onActionClick={() => {
            if (isCostCentersActivated) {
              showSetupGuideModal({
                isOpen: true,
                hasCostCenters: availableCostCenters.length > 0,
              });
            } else {
              showCostCenterActivationModal({
                isOpen: true,
              });
            }
          }}
        />
        {budgetSetupGuideModal}
        {costCenterActivationModal}
        {budgetaryExerciseCreationModal}
      </>
    );
  }

  if (!selectedBudgetaryExerciseId) {
    redirectToBudgetaryExercise(
      getCurrentOrDefaultBudgetaryExercise(
        pageDataQueryState.data.budgetaryExercises,
      )?.id ?? '',
    );
    return null;
  }

  const currentBudgetaryExercise =
    pageDataQueryState.data.budgetaryExercises.find(
      (budgetaryExercise) =>
        budgetaryExercise.id === selectedBudgetaryExerciseId,
    );

  if (!currentBudgetaryExercise) {
    return null;
  }

  const hasBudgets = currentBudgetaryExercise.budgets.length !== 0;

  return (
    <ErrorBoundary context={{ scope: 'budgetary-exercises', team: 'control' }}>
      {canCreateBudgets && !hasCostCenters && !hasBudgets && <NoCostCenter />}
      {!canCreateBudgets &&
        !hasBudgets &&
        t('budget.budgetaryExercise.noDefinedBudgets')}
      <BudgetaryExercisePageContent
        onRangeChange={setSelectedPeriodRange}
        periodRange={selectedPeriodRange}
        hasBudgets={canCreateBudgets && hasCostCenters && hasBudgets}
        budgetaryExercise={currentBudgetaryExercise}
      />
    </ErrorBoundary>
  );
};
